import { useEffect, useMemo, useState } from 'react'
import { Link } from '@/lib/router'
import { api } from '../../api/client'
import { FUNDING_STATUSES, type FundingStatus, type ProjectListItem, type RecentActivity, type Stage } from '../../api/types'
import { useAuth } from '../../auth/useAuth'
import { StageBadge } from '../../components/Badges'
import ScopeNote from '../../components/ScopeNote'
import BarList from '../../components/charts/BarList'
import ChartCard from '../../components/charts/ChartCard'
import { groupByCategory, relationships } from '../../components/charts/chartData'
import ColumnChart from '../../components/charts/ColumnChart'
import NetworkGraph from '../../components/charts/NetworkGraph'
import Treemap from '../../components/charts/Treemap'
import { ACTIVITY_LABELS, FUNDING_LABELS, formatCrore, formatDate, plural, RUPEES_PER_CRORE, toDateOnly } from '../../lib/format'
import { distinct, pipelineLink } from '../pipeline/filters'
import { isActive, startOfToday } from '../focus/ranking'
import {
  applyDashboardFilters,
  countActiveFilters,
  dueSoon,
  followUpBuckets,
  fundingBreakdown,
  NO_FILTERS,
  stageTotal,
  topByValue,
  totalOf,
  valueByState,
  type Bucket,
  type DashboardFilters,
} from './metrics'

const crore = formatCrore
/** "1,350.65" – crore without the ₹ sign and unit, for table columns headed "₹ Cr". */
const croreNumber = (rupees: number) => (rupees / RUPEES_PER_CRORE).toLocaleString('en-IN', { maximumFractionDigits: 2 })

/** Screen 3 – a single-glance view for partner discussions and prioritisation. */
export default function DashboardPage() {
  const { users } = useAuth()
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null)
  const [activities, setActivities] = useState<RecentActivity[]>([])
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<DashboardFilters>(NO_FILTERS)
  const [showFilters, setShowFilters] = useState(false)
  /** Phones only: the charts start folded away (desktop always shows them). */
  const [showCharts, setShowCharts] = useState(false)

  useEffect(() => {
    Promise.all([api<ProjectListItem[]>('/projects'), api<RecentActivity[]>('/activities/recent?limit=100')])
      .then(([p, a]) => {
        setProjects(p)
        setActivities(a)
      })
      .catch((err: Error) => setError(err.message))
  }, [])

  const [today] = useState(startOfToday) // worked out once when the page opens
  const shown = useMemo(() => applyDashboardFilters(projects ?? [], filters), [projects, filters])

  if (error) return <main className="page"><p className="error-text">{error}</p></main>
  if (!projects) return <main className="page"><p className="muted">Loading…</p></main>

  const activeList = shown.filter(isActive)
  const active = totalOf(activeList)
  const categories = groupByCategory(activeList)
  const largest = categories[0]
  const people = relationships(activeList)
  const topPerson = people[0]
  const overdueCount = shown.filter((p) => p.isOverdue).length
  const shownIds = new Set(shown.map((p) => p.id))
  const recent = activities.filter((a) => shownIds.has(a.projectId)).slice(0, 6)
  const soon = dueSoon(shown, today)
  const soonOverdue = soon.filter((p) => p.isOverdue).length

  const followUps = followUpBuckets(shown, today)
  const funding = fundingBreakdown(shown)
  const states = valueByState(shown)
  const top = topByValue(shown)

  const filterCount = countActiveFilters(filters)
  const update = (change: Partial<DashboardFilters>) => setFilters((f) => ({ ...f, ...change }))
  const allLive = projects.filter((p) => !p.isArchived)
  const departments = [...new Map(allLive.map((p) => [p.departmentId, p.departmentName]))].sort((a, b) => a[1].localeCompare(b[1]))

  return (
    <main className="page page-wide dashboard">
      <h1>Pipeline Dashboard</h1>
      <p className="subtitle">Single-glance view for partner discussions and prioritisation.</p>
      <ScopeNote />

      <section className="hero-banner">
        <div>
          <div className="hero-label">Total active pipeline value</div>
          <div className="hero-figure">
            {crore(active.value)} <small>across {plural(active.count, 'active project')}</small>
          </div>
        </div>
        <div className="hero-side">
          <strong>{shown.length}</strong>
          <span>Total tracked projects (all stages)</span>
        </div>
      </section>

      <div className="filter-toggle-row">
        <button className="btn btn-pill" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
          ☰ Filters{filterCount > 0 && ` (${filterCount})`} {showFilters ? '▴' : '▾'}
        </button>
        {filterCount > 0 && (
          <button className="link-button small" onClick={() => setFilters(NO_FILTERS)}>Clear filters</button>
        )}
      </div>
      {showFilters && (
        <div className="card filter-row dashboard-filters">
          <select value={filters.state ?? ''} onChange={(e) => update({ state: e.target.value || null })} aria-label="State">
            <option value="">All states</option>
            {distinct(allLive.map((p) => p.state)).map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.category ?? ''} onChange={(e) => update({ category: e.target.value || null })} aria-label="Category">
            <option value="">All categories</option>
            {distinct(allLive.map((p) => p.category)).map((c) => <option key={c}>{c}</option>)}
          </select>
          <select
            value={filters.departmentId ?? ''}
            onChange={(e) => update({ departmentId: e.target.value ? Number(e.target.value) : null })}
            aria-label="Department"
          >
            <option value="">All departments</option>
            {departments.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select
            value={filters.funding ?? ''}
            onChange={(e) => update({ funding: (e.target.value || null) as FundingStatus | null })}
            aria-label="Funding"
          >
            <option value="">All funding</option>
            {FUNDING_STATUSES.map((f) => <option key={f} value={f}>{FUNDING_LABELS[f]}</option>)}
          </select>
          <select
            value={filters.createdById ?? ''}
            onChange={(e) => update({ createdById: e.target.value ? Number(e.target.value) : null })}
            aria-label="Created by"
          >
            <option value="">Created by anyone</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}

      <div className="stage-tiles">
        {(['Lead', 'Opportunity', 'Tender', 'Won'] as Stage[]).map((stage) => {
          const t = stageTotal(shown, stage)
          return (
            // Opens the Pipeline filtered to this stage, with the Dashboard's own filters carried over,
            // so the list matches the number on the tile.
            <Link
              key={stage}
              to={pipelineLink({ ...filters, stages: [stage] })}
              className={`card stage-tile tile-${stage.toLowerCase()}`}
            >
              <span className="tile-label">● {stage}</span>
              <span className="tile-value">{crore(t.value)}</span>
              <span className="muted small">{plural(t.count, 'project')}</span>
            </Link>
          )
        })}
        <Link to="/overdue" className="card stage-tile tile-overdue">
          <span className="tile-label">● Overdue</span>
          <span className="tile-value">{overdueCount}</span>
          <span className="muted small">follow-ups past due</span>
        </Link>
      </div>

      {/* Phones: the five charts fold away behind one button, so the lists that need action come first. */}
      <button
        className="btn btn-pill phone-only charts-toggle"
        onClick={() => setShowCharts(!showCharts)}
        aria-expanded={showCharts}
      >
        {showCharts ? 'Hide charts ▴' : 'Show charts (5) ▾'}
      </button>

      <div className={showCharts ? 'dashboard-charts open' : 'dashboard-charts'}>
      <div className="chart-grid">
        <ChartCard
          title="Follow-ups coming up"
          subtitle="Active projects by next follow-up date."
          table={bucketTable(followUps, 'When')}
        >
          <ColumnChart
            columns={followUps.map((b) => ({ label: b.label, count: b.count, highlight: b.highlight, detail: crore(b.value) }))}
          />
        </ChartCard>

        <ChartCard
          title="Funding status"
          subtitle="Active pipeline, ₹ crore and project count."
          table={bucketTable(funding, 'Funding status')}
        >
          <BarList rows={funding} formatValue={crore} />
        </ChartCard>

        <ChartCard
          title="Value by state"
          subtitle="Active pipeline, ₹ crore and project count."
          table={bucketTable(states, 'State')}
        >
          <BarList rows={states} formatValue={crore} />
        </ChartCard>
      </div>

      <ChartCard
        title="Where the value sits"
        subtitle="Active pipeline by category. Each block is a project: size = ₹ value, colour = stage. Click a block to open it."
        caption={largest && (
          <>
            <strong>{largest.category}</strong> is the largest block: {crore(largest.value)} (
            {Math.round((largest.value / Math.max(active.value, 1)) * 100)}% of this view).
          </>
        )}
        table={{
          columns: ['Category', 'Project', 'Stage', '₹ crore'],
          rows: categories.flatMap((g) =>
            [...g.projects].sort((a, b) => b.valueRupees - a.valueRupees).map((p) => [g.category, p.title, p.stage, croreNumber(p.valueRupees)]),
          ),
          numeric: [false, false, false, true],
        }}
        className="wide-chart"
      >
        {activeList.length > 0 ? <Treemap projects={activeList} /> : <p className="muted">No active projects match these filters.</p>}
      </ChartCard>

      <ChartCard
        title="Relationship network"
        subtitle="Stakeholders and the active projects they are linked to. Click any node to open it."
        caption={topPerson && (
          <>
            <strong>{topPerson.name}</strong> is the most valuable relationship: linked to{' '}
            {plural(topPerson.projects.length, 'active project')} worth {crore(topPerson.value)}.
          </>
        )}
        table={{
          columns: ['Stakeholder', 'Active projects', '₹ crore', 'Projects'],
          rows: people.map((r) => [r.name, r.projects.length, croreNumber(r.value), r.projects.map((p) => p.title).join('; ')]),
          numeric: [false, true, true, false],
        }}
        className="wide-chart"
      >
        {people.length > 0 ? <NetworkGraph projects={activeList} /> : <p className="muted">No stakeholders are linked to these projects.</p>}
      </ChartCard>
      </div>

      <div className="two-col">
        <section className="card">
          <h2 className="chart-title">Top 10 by value</h2>
          <table className="table compact">
            <thead>
              <tr>
                <th>#</th>
                <th>Project</th>
                <th>Stage</th>
                <th className="num">₹ Cr</th>
              </tr>
            </thead>
            <tbody>
              {top.map((p, i) => (
                <tr key={p.id}>
                  <td className="muted">{i + 1}</td>
                  <td><Link to={`/projects/${p.id}`} className="plain-link">{p.title}</Link></td>
                  <td><StageBadge stage={p.stage} /></td>
                  <td className="num">{croreNumber(p.valueRupees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {top.length === 0 && <p className="muted">No active projects match these filters.</p>}
        </section>

        <section className="card">
          <h2 className="chart-title">Recent activity</h2>
          {recent.length === 0 && <p className="muted">Nothing logged yet.</p>}
          <ol className="recent-list">
            {recent.map((a) => (
              <li key={a.id}>
                <div className="activity-meta">
                  <strong>{formatDate(a.date)}</strong>
                  <span className={`tag tag-${a.type.toLowerCase()}`}>{ACTIVITY_LABELS[a.type]}</span>
                  <span className="muted">{a.loggedByName}</span>
                </div>
                <Link to={`/projects/${a.projectId}`} className="plain-link recent-title">{a.projectTitle}</Link>
                <p className="muted small">{a.note}</p>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <section className="card due-panel">
        <h2 className="chart-title">Follow-ups: overdue &amp; next 14 days</h2>
        <p className="chart-subtitle">
          {soonOverdue} overdue · {soon.length - soonOverdue} more due within 14 days.
        </p>
        {soon.length === 0 ? (
          <p className="muted">Nothing due in the next 14 days.</p>
        ) : (
          <ul className="due-list">
            {soon.map((p) => (
              <li key={p.id}>
                <span>
                  <Link to={`/projects/${p.id}`} className="plain-link"><strong>{p.title}</strong></Link>
                  <span className="muted"> · {p.stage}</span>
                </span>
                <span className={p.isOverdue ? 'due-date overdue' : 'due-date'}>
                  {dueLabel(p, today)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}

function bucketTable(buckets: Bucket[], first: string) {
  return {
    columns: [first, 'Projects', '₹ crore'],
    rows: buckets.map((b) => [b.label, b.count, croreNumber(b.value)]),
    numeric: [false, true, true],
  }
}

function dueLabel(p: ProjectListItem, today: Date): string {
  if (p.isOverdue) return `${formatDate(p.nextFollowUp)} (overdue)`
  return p.nextFollowUp === toDateOnly(today) ? 'Today' : formatDate(p.nextFollowUp)
}
